import { requireField } from './validators';
import Contact from './Contact';
import SafeguardingEvent from './SafeguardingEvent';
import MedicalEvent from './MedicalEvent';
import PoliceEvent from './PoliceEvent';
import AttendanceRecord from './AttendanceRecord';
import Document from './Document';
import Certificate from './Certificate';
import Enrolment from './Enrolment';
import StudentNote from './StudentNote';
import SupportReview from './SupportReview';

export default class StudentModel {
  constructor(data = {}) {
    const derivedName = data.name || [data.firstName, data.middleName, data.lastName].filter(Boolean).join(' ').trim();
    requireField(derivedName, 'student.name');

    this.id = data.id || `stu-${Math.random().toString(36).slice(2, 9)}`;
    this.name = derivedName;
    const nameParts = derivedName.split(' ').filter(Boolean);
    this.firstName = data.firstName || nameParts[0] || '';
    this.lastName = data.lastName || (nameParts.length > 1 ? nameParts[nameParts.length - 1] : '');
    this.middleName =
      data.middleName || (nameParts.length > 2 ? nameParts.slice(1, -1).join(' ') : '');
    this.reference = data.reference || `SP-${Math.floor(Math.random() * 9000 + 1000)}`;
    this.dob = data.dob || null;
    this.age = data.age || null;
    this.pronouns = data.pronouns || '';
    this.school = data.school || '';
    this.phone = data.phone || '';
    this.email = data.email || '';
    this.notesSummary = data.notesSummary || '';
    this.supportAreas = data.supportAreas || [];
    this.status = data.status || 'Active';
    this.riskLevel = data.riskLevel || 'low';
    this.financeStatus = data.financeStatus || 'clear';
    this.finance = data.finance || { courseFees: 0, eventFees: 0, otherFees: 0, outstanding: 0 };
    this.vulnerabilities = data.vulnerabilities || [];
    this.address = data.address || [];
    this.keyWorker = data.keyWorker || null;
    this.supportWorkers = data.supportWorkers || [];
    this.localAuthority = data.localAuthority || null;

    this.contacts = (data.contacts || []).map((c) => new Contact(c));
    this.attendance = new AttendanceRecord(data.attendance || { rate: 0 });

    this.safeguardingEvents = (data.safeguardingEvents || []).map(
      (e) => new SafeguardingEvent(e)
    );
    this.medicalEvents = (data.medicalEvents || []).map((e) => new MedicalEvent(e));
    this.policeEvents = (data.policeEvents || []).map((e) => new PoliceEvent(e));
    this.documents = (data.documents || []).map((d) => new Document(d));
    this.certificates = (data.certificates || []).map((c) => new Certificate(c));
    this.enrolments = (data.enrolments || []).map((e) => new Enrolment(e));
    this.notes = (data.notes || []).map((n) => new StudentNote(n));
    this.supportReviews = (data.supportReviews || []).map((r) => new SupportReview(r));
    this.incidentIds = data.incidentIds || [];
    this.incidentReports = data.incidentReports || [];
    this.activities = data.activities || [];

    this.portalPasswordHash = data.portalPasswordHash || '';
    this.portalPasswordMustChange = Boolean(data.portalPasswordMustChange);
    this.portalResetTokenHash = data.portalResetTokenHash || '';
    this.portalResetExpiresAt = data.portalResetExpiresAt || null;

    this.statusHistory = data.statusHistory || [];
    this.lastUpdated = data.lastUpdated || new Date().toISOString();
  }

  get lastSafeguardingEventDate() {
    const dates = this.safeguardingEvents.map((e) => e.date).filter(Boolean);
    return dates.sort().slice(-1)[0] || null;
  }

  get activeSafeguardingCount() {
    return this.safeguardingEvents.filter((e) => e.status === 'open').length;
  }

  get notesSorted() {
    return [...this.notes].sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
  }

  toJSON() {
    return {
      ...this,
      contacts: this.contacts.map((c) => c.toJSON()),
      attendance: this.attendance.toJSON(),
      safeguardingEvents: this.safeguardingEvents.map((e) => e.toJSON()),
      medicalEvents: this.medicalEvents.map((e) => e.toJSON()),
      policeEvents: this.policeEvents.map((e) => e.toJSON()),
      documents: this.documents.map((d) => d.toJSON()),
      certificates: this.certificates.map((c) => c.toJSON()),
      enrolments: this.enrolments.map((e) => e.toJSON()),
      notes: this.notes.map((n) => n.toJSON()),
      supportReviews: this.supportReviews.map((r) => r.toJSON()),
      incidentIds: this.incidentIds,
      incidentReports: this.incidentReports,
      activities: this.activities,
      finance: this.finance
    };
  }
}
